import React, { useState, useEffect, useRef } from 'react';
import { Search, ShoppingBag, Heart, Menu, X, User, Zap, ArrowRight, Shield, Layout, LogOut, Gamepad2, FileText, Globe, ChevronDown, ChevronUp } from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { setHeaderSearchQuery, setSearchQuery, fetchGames, setSelectedCategory, fetchSearchGames, setSearchGames } from '../../redux/slice/game.slice';
import { toggleCart, toggleWishlistDrawer } from '../../redux/slice/user.slice';
import { logout } from '../../redux/slice/auth.slice';
import { fetchBlogsThunk } from '../../redux/slice/blog.slice';
import { fetchAllCategories } from '../../redux/slice/category.slice';
import gsap from 'gsap';
import LogoHeader from '../../assets/logo-header.svg';
import LogoFav from '../../assets/fav.svg';
import LogoutModal from '../common/LogoutModal';

export default function Header() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { user, isAuthenticated } = useSelector((s) => s.auth);
  const { headerSearchQuery, searchGames } = useSelector((s) => s.game);
  const { blogs } = useSelector((s) => s.blog);
  const { categories } = useSelector((s) => s.category);
  const { cart, wishlist } = useSelector((s) => s.userSlice);

  const cartItemCount = cart.reduce((total, item) => total + (item.quantity || 1), 0);

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [activeSection, setActiveSection] = useState('pages');

  const headerRef = useRef(null);
  const navRef = useRef(null);
  const inputRef = useRef(null);
  const suggestionRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    dispatch(fetchBlogsThunk({ limit: 20 }));
    dispatch(fetchAllCategories());
  }, [dispatch]);

  useEffect(() => {
    const query = headerSearchQuery.trim();
    if (query.length < 3) {
      dispatch(setSearchGames(null));
      return undefined;
    }
    const timer = setTimeout(() => {
      dispatch(fetchSearchGames({ search: query }));
    }, 300);
    return () => clearTimeout(timer);
  }, [headerSearchQuery, dispatch]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.altKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
      }
      if (e.key === 'Escape') {
        setShowSuggestions(false);
        dispatch(setHeaderSearchQuery(''));
        inputRef.current?.blur();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [dispatch]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // If the clicked element is no longer in the document body, it was likely unmounted during click (e.g. accordion icon toggle). Do not close.
      if (!document.body.contains(event.target)) {
        return;
      }
      if (suggestionRef.current && !suggestionRef.current.contains(event.target) && !inputRef.current.contains(event.target)) {
        setShowSuggestions(false);
        setIsSearchFocused(false);
        dispatch(setHeaderSearchQuery(''));
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dispatch]);

  const query = headerSearchQuery.trim().toLowerCase();

  const pagesList = query ? [
    { name: 'Home', path: '/', type: 'page' },
    { name: 'Game Catalog', path: '/all-games', type: 'page' },
    { name: 'Game Platforms', path: '/platforms', type: 'page' },
    { name: 'Blog Updates', path: '/blog', type: 'page' },
    { name: 'Contact Us', path: '/contact', type: 'page' },
  ].filter(p => p.name.toLowerCase().includes(query)) : [];

  const gamesList = query.length >= 3 ? (searchGames || []).map(g => ({
    name: g.title,
    path: `/game/${g._id}`,
    category: g.category?.name || 'Uncategorized',
    type: 'game'
  })) : [];

  const categoryList = query ? (categories || []).filter(c => c.name.toLowerCase().includes(query)).map(c => ({
    name: c.name,
    path: `/all-games`,
    type: 'category'
  })) : [];

  const blogsList = query ? (blogs || []).filter(b => b.title.toLowerCase().includes(query)).map(b => ({
    name: b.title,
    path: `/blog/${b._id}`,
    type: 'blog'
  })) : [];

  const otherList = query ? [
    { name: 'FAQ', path: '/faq', type: 'other' },
    { name: 'Privacy Policy', path: '/privacy', type: 'other' },
    { name: 'Terms of Service', path: '/terms', type: 'other' },
  ].filter(o => o.name.toLowerCase().includes(query)) : [];

  const visibleSuggestions = [];
  if (activeSection === 'pages') visibleSuggestions.push(...pagesList);
  if (activeSection === 'games') visibleSuggestions.push(...gamesList);
  if (activeSection === 'category') visibleSuggestions.push(...categoryList);
  if (activeSection === 'blogs') visibleSuggestions.push(...blogsList);
  if (activeSection === 'other') visibleSuggestions.push(...otherList);

  useEffect(() => {
    const hasAnyResults = pagesList.length > 0 || gamesList.length > 0 || categoryList.length > 0 || blogsList.length > 0 || otherList.length > 0;
    if (query.length >= 1 && hasAnyResults) {
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  }, [headerSearchQuery, searchGames, blogs, categories]);

  useEffect(() => {
    if (headerSearchQuery.trim()) {
      const sections = [
        { key: 'pages', list: pagesList },
        { key: 'games', list: gamesList },
        { key: 'category', list: categoryList },
        { key: 'blogs', list: blogsList },
        { key: 'other', list: otherList }
      ];
      const currentActiveEmpty = sections.find(s => s.key === activeSection)?.list.length === 0;
      if (currentActiveEmpty || !activeSection) {
        const firstNonEmpty = sections.find(s => s.list.length > 0);
        if (firstNonEmpty) {
          setActiveSection(firstNonEmpty.key);
        }
      }
    } else {
      setActiveSection('pages');
    }
  }, [searchGames, blogs, categories, headerSearchQuery]);

  const toggleSection = (section) => {
    setActiveSection(prev => prev === section ? '' : section);
  };

  const renderAccordionSection = (title, items, type, isOpen, toggleKey) => {
    if (items.length === 0) return null;

    return (
      <div className="border-b border-white/5 last:border-b-0">
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            toggleSection(toggleKey);
          }}
          className="w-full flex items-center justify-between px-4 py-2.5 bg-white/[0.01] hover:bg-white/[0.03] transition-colors"
        >
          <span className="text-[10px] font-cyber font-bold text-gray-400 uppercase tracking-widest flex items-center gap-1.5">
            {title}
            <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-white/5 text-gray-500 font-normal">
              {items.length}
            </span>
          </span>
          {isOpen ? (
            <ChevronUp className="w-3.5 h-3.5 text-gray-500" />
          ) : (
            <ChevronDown className="w-3.5 h-3.5 text-gray-500" />
          )}
        </button>

        {isOpen && (
          <div className="py-1 bg-black/10">
            {items.map((item) => {
              const flatIndex = visibleSuggestions.findIndex(
                (v) => v.path === item.path && v.name === item.name
              );
              const isActive = flatIndex === activeIndex;

              return (
                <button
                  key={item.path + item.name}
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleSuggestionClick(item);
                  }}
                  className={`w-full flex items-center gap-3 px-6 py-2 text-left transition-colors ${
                    isActive ? 'bg-white/10' : 'hover:bg-white/5'
                  } group/item`}
                >
                  <div
                    className={`w-6 h-6 rounded flex items-center justify-center ${
                      type === 'page'
                        ? 'bg-cyber-blue/10 text-cyber-blue'
                        : type === 'game'
                        ? 'bg-cyber-purple/10 text-cyber-purple'
                        : type === 'category'
                        ? 'bg-cyber-cyan/10 text-cyber-cyan'
                        : type === 'blog'
                        ? 'bg-cyber-pink/10 text-cyber-pink'
                        : 'bg-white/10 text-gray-400'
                    }`}
                  >
                    {type === 'page' ? (
                      <Globe className="w-3.5 h-3.5" />
                    ) : type === 'game' ? (
                      <Gamepad2 className="w-3.5 h-3.5" />
                    ) : type === 'category' ? (
                      <Layout className="w-3.5 h-3.5" />
                    ) : type === 'blog' ? (
                      <FileText className="w-3.5 h-3.5" />
                    ) : (
                      <Zap className="w-3.5 h-3.5" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10.5px] font-cyber font-medium text-gray-300 group-hover:text-white truncate uppercase tracking-wider">
                      {item.name}
                    </p>
                    {item.category && (
                      <p className="text-[8px] text-gray-500 uppercase tracking-widest font-mono">
                        {item.category}
                      </p>
                    )}
                  </div>
                  <ArrowRight className="w-3 h-3 text-gray-600 opacity-0 group-hover:text-cyber-cyan group-hover:opacity-100 transition-all" />
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const handleSuggestionClick = (item) => {
    if (item.type === 'category') {
      dispatch(setSelectedCategory(item.name));
    }
    navigate(item.path);
    setShowSuggestions(false);
    setIsSearchFocused(false);
    dispatch(setHeaderSearchQuery(''));
    inputRef.current?.blur();
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((prev) => (prev < visibleSuggestions.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((prev) => (prev > 0 ? prev - 1 : -1));
    } else if (e.key === 'Enter') {
      if (activeIndex >= 0 && activeIndex < visibleSuggestions.length) {
        handleSuggestionClick(visibleSuggestions[activeIndex]);
      } else if (visibleSuggestions.length > 0) {
        handleSuggestionClick(visibleSuggestions[0]);
      } else if (headerSearchQuery.trim()) {
        dispatch(setSearchQuery(headerSearchQuery));
        navigate('/all-games');
        setShowSuggestions(false);
        setIsSearchFocused(false);
        inputRef.current?.blur();
      }
    }
  };

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(headerRef.current,
        { y: -100, opacity: 0 },
        { y: 0, opacity: 1, duration: 1, ease: "expo.out" }
      );

      gsap.from('.nav-link', {
        y: -20,
        opacity: 0,
        duration: 0.8,
        stagger: 0.1,
        ease: "power3.out",
        delay: 0.5
      });
    });
    return () => ctx.revert();
  }, []);

  const NavLink = ({ to, children, icon: Icon, color }) => {
    const isActive = location.pathname === to;
    return (
      <Link
        to={to}
        className={`nav-link group relative flex items-center gap-2 px-4 py-2 rounded-xl transition-all duration-300 ${isActive ? 'text-white' : 'text-gray-400 hover:text-white'
          }`}
      >
        {Icon && <Icon className={`w-4 h-4 transition-colors ${isActive ? color : 'group-hover:' + color}`} />}
        <span className="text-[11px] font-cyber font-medium uppercase tracking-[0.2em]">{children}</span>
        {isActive && (
          <span className={`absolute bottom-0 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full ${color.replace('text-', 'bg-')} shadow-[0_0_8px_currentColor]`}></span>
        )}
      </Link>
    );
  };

  return (
    <>
      <header
        ref={headerRef}
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 border-b ${
          scrolled
            ? 'bg-[#050508]/80 backdrop-blur-lg border-white/10 shadow-[0_10px_30px_rgba(0,0,0,0.8)] py-2'
            : 'bg-transparent border-transparent py-2'
        }`}
      >
        <div className={`container mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-500`}>
          <div className={`relative flex items-center justify-between `}>

            {/* Logo */}
            <Link to="/" className={`flex items-center group flex-shrink-0 transition-all ${isSearchFocused ? 'hidden md600:flex' : 'flex'}`}>
              <div className="relative h-12 sm:h-14 md:h-12 lg:h-16 flex items-center justify-center transition-all duration-500 min-w-[50px] md600:min-w-[140px]">
                {/* Full Logo for Desktop/Tablet */}
                <img
                  src={LogoHeader}
                  alt="CUBE GAMES"
                  className="h-full w-auto object-contain filter group-hover:brightness-110 transition-all duration-500 hidden md600:block"
                />
                {/* Compact Logo for Mobile */}
                <img
                  src={LogoFav}
                  alt="CUBE GAMES"
                  className="h-10 w-auto object-contain filter group-hover:brightness-110 transition-all duration-500 md600:hidden block"
                />
              </div>
            </Link>

            {/* Desktop Nav */}
            <nav className={`hidden xl:flex items-center gap-2 rounded-[2rem] border transition-all duration-500 px-4 h-12 ${scrolled
              ? 'bg-[#050508]/80 backdrop-blur-2xl border-white/10 shadow-[0_20px_50px_rgba(0,0,0,0.5)]'
              : 'bg-transparent border-transparent'
              }`}>
              <NavLink to="/" color="text-cyber-blue">Home</NavLink>
              <NavLink to="/all-games" color="text-cyber-purple">Games</NavLink>
              <NavLink to="/platforms" color="text-cyber-cyan">Platforms</NavLink>
              <NavLink to="/contact" color="text-cyber-pink">Contact</NavLink>
              <NavLink to="/blog" color="text-cyber-pink">Blogs</NavLink>
            </nav>

            {/* Actions */}
            <div className={`flex items-center gap-1.5 sm:gap-3 justify-end ${isSearchFocused ? 'flex-1 md600:flex-none' : ''}`}>
              <div
                onClick={() => inputRef.current?.focus()}
                className={`transition-all !outline-none ${isSearchFocused ? 'duration-0 md600:duration-500' : 'duration-500'} ${isSearchFocused
                  ? 'flex-1 flex items-center rounded-xl border-transparent md600:relative md600:w-64 md600:flex-none'
                  : 'relative flex items-center justify-center cursor-pointer w-10 h-10 rounded-xl bg-white/5 border border-white/5 text-gray-400 hover:text-cyber-cyan hover:border-cyber-cyan/20 transition-all'
                  }`}
              >
                <Search className={`w-4 h-4 transition-all duration-500 ${isSearchFocused ? 'absolute left-5 text-cyber-cyan' : 'text-gray-500'}`} />
                <input
                  ref={inputRef}
                  type="text"
                  placeholder={isSearchFocused ? "Search store..." : "Search..."}
                  value={headerSearchQuery}
                  onChange={(e) => {
                    dispatch(setHeaderSearchQuery(e.target.value));
                  }}
                  onFocus={() => {
                    setIsSearchFocused(true);
                    if (headerSearchQuery.trim()) setShowSuggestions(true);
                  }}
                  onBlur={() => {
                    // Delay slightly to check if the focus went to the suggestions dropdown
                    setTimeout(() => {
                      if (suggestionRef.current && suggestionRef.current.contains(document.activeElement)) {
                        return; // Keep focus state
                      }
                      if (window.innerWidth >= 601) setIsSearchFocused(false);
                    }, 150);
                  }}
                  onKeyDown={handleSearchKeyDown}
                  className={`w-full bg-white/5 border border-white/10 rounded-xl py-2.5 pl-12 pr-10 text-xs font-cyber text-white !outline-none focus:!outline-none transition-all focus:bg-white/10 focus:border-cyber-cyan/40 ${isSearchFocused ? 'opacity-100' : 'opacity-0 pointer-events-none absolute'}`}
                />
                {isSearchFocused && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsSearchFocused(false);
                      setShowSuggestions(false);
                      dispatch(setHeaderSearchQuery(''));
                    }}
                    className="md600:hidden absolute right-4 p-2 text-gray-400 hover:text-white"
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}

                {/* Suggestions Dropdown */}
                {showSuggestions && isSearchFocused && (
                  <div
                    ref={suggestionRef} data-lenis-prevent
                    className="absolute top-full left-0 right-0 mt-2 bg-[#090915]/95 backdrop-blur-2xl border border-white/10 rounded-xl shadow-2xl overflow-hidden z-[100] max-h-96 overflow-y-auto no-scrollbar animate-in fade-in slide-in-from-top-2 duration-200"
                  >
                    <div className="py-2 flex flex-col">
                      {renderAccordionSection('Pages', pagesList, 'page', activeSection === 'pages', 'pages')}
                      {renderAccordionSection('Games', gamesList, 'game', activeSection === 'games', 'games')}
                      {renderAccordionSection('Categories', categoryList, 'category', activeSection === 'category', 'category')}
                      {renderAccordionSection('Blogs', blogsList, 'blog', activeSection === 'blogs', 'blogs')}
                      {renderAccordionSection('Other', otherList, 'other', activeSection === 'other', 'other')}
                    </div>
                  </div>
                )}
              </div>

              {/* Wishlist */}
              <button
                onClick={() => navigate('/wishlist')}
                className={`${isSearchFocused ? 'hidden' : 'hidden md:flex'} relative p-2.5 rounded-xl bg-white/5 border border-white/5 text-gray-400 hover:text-cyber-pink hover:border-cyber-pink/20 transition-all`}
              >
                <Heart className={`w-4 h-4 ${wishlist.length > 0 ? 'fill-cyber-pink text-cyber-pink' : ''}`} />
                {wishlist.length > 0 && <span className="absolute -top-1 -right-1 w-2 h-2 bg-cyber-pink rounded-full shadow-[0_0_8px_#ff007a]"></span>}
              </button>

              {/* Cart */}
              <button
                onClick={() => dispatch(toggleCart())}
                className={`${isSearchFocused ? 'hidden' : 'hidden md:flex'} relative p-2.5 rounded-xl bg-white/5 border border-white/5 text-gray-400 hover:text-cyber-blue hover:border-cyber-blue/20 transition-all`}
              >
                <ShoppingBag className="w-4 h-4" />
                {cartItemCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-cyber-blue text-black font-cyber font-semibold text-[9px] w-5 h-5 rounded-lg flex items-center justify-center shadow-[0_0_10px_#00f0ff]">
                    {cartItemCount}
                  </span>
                )}
              </button>

              {/* User */}
              <div className={`relative ml-2 ${isSearchFocused ? 'hidden' : 'block'}`}>
                {isAuthenticated ? (
                  <button
                    onClick={() => setIsProfileOpen(!isProfileOpen)}
                    className="flex items-center gap-2 p-1.5 md:px-3 md:py-1.5 rounded-xl bg-white/5 border border-white/10 hover:border-cyber-cyan/30 hover:bg-white/10 transition-all duration-300 group"
                  >
                    <div className="w-7 h-7 rounded-lg bg-gradient-to-tr from-cyber-blue to-cyber-purple p-[1px] overflow-hidden flex-shrink-0 flex items-center justify-center">
                      <div className="w-full h-full rounded-[7px] bg-[#0c0c18] flex items-center justify-center overflow-hidden">
                        {user?.photo ? (
                          <img src={user.photo} alt="user" className="w-full h-full object-cover" />
                        ) : (
                          <span className="font-cyber font-semibold text-[10px] text-cyber-cyan tracking-tighter">
                            {user?.firstName ? user.firstName.charAt(0).toUpperCase() : 'U'}
                          </span>
                        )}
                      </div>
                    </div>
                    <span className="text-xs font-cyber font-medium text-gray-300 group-hover:text-white tracking-wide uppercase transition-colors hidden md:inline-block">
                      {user?.firstName}
                    </span>
                  </button>
                ) : (
                  <button
                    onClick={() => setIsProfileOpen(!isProfileOpen)}
                    className="w-10 h-10 rounded-xl bg-gradient-to-tr from-cyber-blue/20 to-cyber-purple/20 border border-white/10 flex items-center justify-center hover:border-cyber-cyan/40 transition-all overflow-hidden"
                  >
                    <User className="w-5 h-5 text-cyber-cyan" />
                  </button>
                )}

                {isProfileOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setIsProfileOpen(false)}></div>
                    <div className="absolute right-0 mt-4 sm:w-64 w-52 rounded-2xl bg-[#090915]/95 backdrop-blur-2xl border border-white/10 p-3 shadow-[0_20px_50px_rgba(0,0,0,0.6)] animate-fade-in z-50 overflow-hidden">
                      <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-cyber-blue to-cyber-purple"></div>

                      {isAuthenticated ? (
                        <div className="space-y-2">
                          <div className="px-3 py-3 rounded-xl bg-white/[0.02] border border-white/5 mb-2">
                            <div className="flex items-center gap-2.5">
                              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyber-blue/30 to-cyber-purple/30 border border-white/10 flex items-center justify-center overflow-hidden flex-shrink-0">
                                {user?.photo ? (
                                  <img src={user.photo} alt="user" className="w-full h-full object-cover" />
                                ) : (
                                  <span className="font-cyber font-semibold text-xs text-cyber-cyan">
                                    {user?.firstName ? user.firstName.charAt(0).toUpperCase() : 'U'}
                                  </span>
                                )}
                              </div>
                              <div className="min-w-0">
                                <p className="text-xs font-cyber font-semibold text-white truncate uppercase tracking-wide">
                                  {user?.firstName} {user?.lastName}
                                </p>
                                <p className="text-[10px] font-mono text-gray-500 truncate mt-0.5">
                                  {user?.email}
                                </p>
                              </div>
                            </div>

                            {/* Role badge if Admin */}
                            {user?.role?.toLowerCase() === 'admin' && (
                              <div className="mt-2.5 flex items-center gap-1.5 px-2 py-1 rounded bg-cyber-pink/10 border border-cyber-pink/20 text-cyber-pink text-[9px] font-cyber font-medium uppercase tracking-widest justify-center shadow-[0_0_8px_rgba(255,0,128,0.1)]">
                                <Shield className="w-3 h-3" /> Admin
                              </div>
                            )}
                          </div>

                          <div className="space-y-1">
                            {/* Wishlist & Cart - Visible on mobile/tablet when hidden from main header */}
                            <div className="md:hidden space-y-1">
                              <button
                                onClick={() => { navigate('/wishlist'); setIsProfileOpen(false); }}
                                className="w-full flex items-center gap-3 px-3 py-2.5 text-xs text-gray-400 hover:text-white hover:bg-white/5 rounded-xl transition-all group"
                              >
                                <Heart className={`w-4 h-4 ${wishlist.length > 0 ? 'fill-cyber-pink text-cyber-pink' : 'text-gray-500'} group-hover:text-cyber-pink transition-colors`} />
                                <span className="font-cyber font-medium tracking-wider uppercase">Wishlist ({wishlist.length})</span>
                              </button>

                              <button
                                onClick={() => { dispatch(toggleCart()); setIsProfileOpen(false); }}
                                className="w-full flex items-center gap-3 px-3 py-2.5 text-xs text-gray-400 hover:text-white hover:bg-white/5 rounded-xl transition-all group"
                              >
                                <ShoppingBag className="w-4 h-4 text-gray-500 group-hover:text-cyber-blue transition-colors" />
                                <span className="font-cyber font-medium tracking-wider uppercase">My Cart ({cartItemCount})</span>
                              </button>
                              <div className="h-[1px] bg-white/5 my-1"></div>
                            </div>

                            <button
                              onClick={() => { navigate('/profile'); setIsProfileOpen(false); }}
                              className="w-full flex items-center gap-3 px-3 py-2.5 text-xs text-gray-400 hover:text-white hover:bg-white/5 rounded-xl transition-all group"
                            >
                              <User className="w-4 h-4 text-gray-500 group-hover:text-cyber-cyan transition-colors" />
                              <span className="font-cyber font-medium tracking-wider uppercase">My Profile</span>
                            </button>

                            {user?.role?.toLowerCase() === 'admin' && (
                              <button
                                onClick={() => { navigate('/admin'); setIsProfileOpen(false); }}
                                className="w-full flex items-center gap-3 px-3 py-2.5 text-xs text-gray-400 hover:text-white hover:bg-white/5 rounded-xl transition-all group"
                              >
                                <Layout className="w-4 h-4 text-gray-500 group-hover:text-cyber-pink transition-colors" />
                                <span className="font-cyber font-medium tracking-wider uppercase">Admin Panel</span>
                              </button>
                            )}

                            <div className="h-[1px] bg-white/5 my-1"></div>

                            <button
                              onClick={() => { setShowLogoutModal(true); setIsProfileOpen(false); }}
                              className="w-full flex items-center gap-3 px-3 py-2.5 text-xs text-cyber-pink hover:bg-cyber-pink/10 rounded-xl transition-all group"
                            >
                              <LogOut className="w-4 h-4 text-cyber-pink/70 group-hover:text-cyber-pink transition-colors" />
                              <span className="font-cyber font-medium tracking-wider">Sign Out</span>
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="p-2 space-y-2">
                          <button onClick={() => { navigate('/login', { state: { activeTab: 'login' } }); setIsProfileOpen(false); }} className="cyber-btn w-full !py-3 !text-[10px]">Sign In</button>
                          <button onClick={() => { navigate('/login', { state: { activeTab: 'register' } }); setIsProfileOpen(false); }} className="w-full py-3 rounded-xl bg-white/5 border border-white/10 text-white font-cyber font-semibold text-[10px] uppercase tracking-widest hover:bg-white/10">Register</button>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>

              {/* Mobile Menu */}
              <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className={`xl:hidden p-2.5 rounded-xl bg-white/5 border border-white/10 text-gray-400 ${isSearchFocused ? 'hidden md600:block' : 'block'}`}>
                {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-[60] bg-[#050508]/95 backdrop-blur-3xl xl:hidden flex flex-col p-8 animate-fade-in">
          <div className="flex items-center justify-between mb-12">
            <img src={LogoHeader} alt="CUBE GAMES" className="h-9 w-auto object-contain" />
            <button onClick={() => setIsMobileMenuOpen(false)} className="p-2 rounded-xl bg-white/5 border border-white/10 text-white">
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="flex flex-col gap-6">
            {['Home', 'Games', 'Platforms', 'Contact', 'Blogs'].map((item, i) => (
              <button
                key={item}
                onClick={() => {
                  if (item === 'Games') navigate('/all-games');
                  else if (item === 'Platforms') navigate('/platforms');
                  else if (item === 'Home') navigate('/');
                  else if (item === 'Contact') navigate('/contact');
                  else if (item === 'Blogs') navigate('/blog');
                  setIsMobileMenuOpen(false);
                }}
                className="text-left group"
              >
                <div className="text-[10px] text-gray-500 font-cyber font-medium tracking-widest mb-1">0{i + 1}</div>
                <div className="text-3xl font-cyber font-semibold text-white group-hover:text-cyber-cyan transition-all uppercase tracking-tighter">{item}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Logout Confirmation Modal */}
      <LogoutModal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={() => {
          dispatch(logout());
          navigate('/');
          setShowLogoutModal(false);
        }}
      />
    </>
  );
}
